import React, { useContext } from 'react';
import { Select, Stack } from '@shopify/polaris';
import { AppContext } from '../global/appContext';

/**
 * Reusable Language Selector Component
 * Provides consistent language selection UI across all forms
 * 
 * @param {Object} props
 * @param {Array} props.options - Array of locale options {label, value}
 * @param {string} props.value - Currently selected locale
 * @param {Function} props.onChange - Callback when locale changes
 * @param {boolean} props.showIcon - Whether to show the language icon (default: true)
 * @param {boolean} props.labelHidden - Whether to hide the label (default: true)
 * @param {string} props.label - Label for the select (default: "")
 * @param {string} props.helpText - Help text for the select
 * @param {boolean} props.disabled - Whether the selector is disabled
 * @param {string} props.minWidth - Minimum width for the selector (default: '150px')
 */
export default function LanguageSelector({
  options = [],
  value,
  onChange,
  showIcon = true,
  labelHidden = true,
  label = "",
  helpText,
  disabled = false,
  minWidth = '150px',
  ...selectProps
}) {
  const { languageTranslateIconUrl } = useContext(AppContext);

  if (options.length <= 1) {
    return null; // Don't show selector if only one language available
  }

  const selectComponent = (
    <div style={{ minWidth }}>
      <Select
        label={label}
        labelHidden={labelHidden}
        options={options}
        value={value}
        onChange={onChange}
        helpText={helpText}
        disabled={disabled}
        {...selectProps}
      />
    </div>
  );

  if (!showIcon) {
    return selectComponent;
  }

  return (
    <Stack alignment="center" spacing="tight">
      <img 
        src={languageTranslateIconUrl || '/assets/LanguageTranslateIcon.svg'} 
        alt="Language" 
        style={{ width: '20px', height: '20px', opacity: 0.6 }} 
      />
      {selectComponent}
    </Stack>
  );
} 